import { t } from "../i18n"

export type SignInLocationStatus = "granted" | "denied" | "unavailable" | "timeout"

export interface SignInLocationSource {
  clockInLatitude?: number | string | null
  clockInLongitude?: number | string | null
  clockInAccuracyM?: number | string | null
  clockInLocationAt?: string | Date | null
  clockInLocationStatus?: string | null
}

export type SignInLocationView =
  | { kind: "point", latitude: number, longitude: number, accuracyM: number | null, mapsUrl: string, label: string }
  | { kind: "status", status: SignInLocationStatus | "missing", label: string }

function num(value: unknown): number | null {
  if (value == null || value === "") return null
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Turn the clock-in location columns into something the time sheet can show.
 * Coordinates win; otherwise the recorded status (denied etc.) is shown.
 * Older work days with nothing recorded show "Not recorded".
 */
export function describeSignInLocation(source: SignInLocationSource | null | undefined): SignInLocationView {
  const latitude = num(source?.clockInLatitude)
  const longitude = num(source?.clockInLongitude)
  if (
    latitude != null && longitude != null
    && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
  ) {
    const accuracyM = num(source?.clockInAccuracyM)
    const coords = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
    return {
      kind: "point",
      latitude,
      longitude,
      accuracyM,
      mapsUrl: `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`,
      label: accuracyM != null
        ? t("signInLocation.withAccuracy", { coords, meters: Math.round(accuracyM) })
        : coords,
    }
  }
  const raw = (source?.clockInLocationStatus ?? "").toLowerCase()
  if (raw === "denied") return { kind: "status", status: "denied", label: t("signInLocation.denied") }
  if (raw === "timeout") return { kind: "status", status: "timeout", label: t("signInLocation.timeout") }
  if (raw === "unavailable" || raw === "granted") {
    return { kind: "status", status: "unavailable", label: t("signInLocation.unavailable") }
  }
  return { kind: "status", status: "missing", label: t("signInLocation.missing") }
}
